import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useAppTranslation } from '../../services/i18n';
import { WorkplaceNode } from '../../helpers/workplaceTree';
import { OrganizationUnitsMembershipSummaryMembershipSummaryResponse } from '../../generated-api';

export type MembershipSummary = OrganizationUnitsMembershipSummaryMembershipSummaryResponse;

/** walking the tree with the arrows passes many workplaces; only a pause asks the server */
const SUMMARY_DELAY = 250;

interface Props {
  node: WorkplaceNode;
  /** a flat list has no subordinates, so every subtree figure is left out */
  isTreeEnabled: boolean;
  /** only then can a member be on other workplaces too */
  isMultipleWorkplacesEnabled: boolean;
  /** the tab is on screen; the summary costs a request, so it waits until then */
  isActive: boolean;
  /** undefined when the request failed; the error itself is reported globally */
  onLoadSummary: (id: number) => Promise<MembershipSummary | undefined>;
  /** leaves the editor, which first asks about unsaved changes in an open form */
  onNavigate: (href: string) => void;
}

/** read-only overview of who belongs to the workplace, per the Membership tab design */
export const WorkplaceMembership: React.FC<Props> = ({
  node,
  isTreeEnabled,
  isMultipleWorkplacesEnabled,
  isActive,
  onLoadSummary,
  onNavigate,
}) => {
  const t = useAppTranslation();
  const [summary, setSummary] = useState<MembershipSummary | undefined>(undefined);
  const [isFailed, setIsFailed] = useState(false);
  /** the unit record the summary belongs to; a refetched tree hands in a new one */
  const loadedFor = useRef<unknown>(undefined);

  useEffect(() => {
    if (!isActive || loadedFor.current === node.unit) {
      return;
    }
    loadedFor.current = node.unit;
    let isCurrent = true;
    let isSettled = false;
    setSummary(undefined);
    setIsFailed(false);
    const timer = window.setTimeout(() => {
      onLoadSummary(node.id).then((result) => {
        // an answer for a workplace the user has already moved past is dropped
        if (!isCurrent) {
          return;
        }
        isSettled = true;
        setSummary(result);
        setIsFailed(!result);
      });
    }, SUMMARY_DELAY);
    return () => {
      isCurrent = false;
      window.clearTimeout(timer);
      // an answer that never landed must not count as loaded
      if (!isSettled && loadedFor.current === node.unit) {
        loadedFor.current = undefined;
      }
    };
  }, [isActive, node.id, node.unit, onLoadSummary]);

  const usersHref =
    '#/userManagement/users?workplace=' +
    node.id +
    '&workplaceName=' +
    encodeURIComponent(node.unit.name) +
    (isTreeEnabled && node.children.length > 0 ? '&subtree=true' : '');

  const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // a modified click opens another tab and loses nothing, so the browser keeps it
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    onNavigate(usersHref);
  };

  const multiple = summary?.multiple_workplaces;
  const jobTitles = summary
    ? [...summary.job_titles]
        .filter((row) => isTreeEnabled || row.direct_count > 0)
        .sort(
          (a, b) =>
            b.direct_count - a.direct_count ||
            b.subtree_count - a.subtree_count ||
            a.name.localeCompare(b.name)
        )
    : [];
  const withoutJobTitle = summary?.without_job_title;
  const showWithoutJobTitle =
    !!withoutJobTitle &&
    (withoutJobTitle.direct_count > 0 || (isTreeEnabled && withoutJobTitle.subtree_count > 0));

  return (
    <section className={'workplace-membership'}>
      <div className={'workplace-membership__head'}>
        <h4 className={'workplace-membership__title'}>{t('workplaces.detail.membership')}</h4>
        <span className={'workplace-panel__spacer'} />
        <a className={'workplace-membership__link'} href={usersHref} onClick={handleLinkClick}>
          {t('workplaces.membership.showUsers')}
        </a>
      </div>
      <div className={'workplace-membership__body'}>
        <div className={'workplace-membership__tiles'}>
          <div className={'workplace-membership__tile'}>
            <span className={'workplace-membership__label'}>
              {t(isTreeEnabled ? 'workplaces.membership.direct' : 'workplaces.membership.members')}
            </span>
            <span className={'workplace-membership__value'}>{node.userCount}</span>
            <span className={'workplace-membership__hint'}>
              {t(
                isTreeEnabled
                  ? 'workplaces.membership.directHint'
                  : 'workplaces.membership.membersHint',
                { count: node.userCount }
              )}
            </span>
          </div>
          {isTreeEnabled && (
            <div className={'workplace-membership__tile'}>
              <span className={'workplace-membership__label'}>
                {t('workplaces.membership.subtree')}
              </span>
              <span className={'workplace-membership__value'}>{node.totalUserCount}</span>
              <span className={'workplace-membership__hint'}>
                {t('workplaces.membership.subtreeHint', { count: node.totalUserCount })}
              </span>
            </div>
          )}
          {isMultipleWorkplacesEnabled && (
            <div className={'workplace-membership__tile'}>
              <span className={'workplace-membership__label'}>
                {t('workplaces.membership.multiple')}
              </span>
              <span className={'workplace-membership__value'}>{multiple ? multiple.count : '–'}</span>
              <span className={'workplace-membership__hint'}>
                {multiple && (
                  <Trans
                    i18nKey={'workplaces.membership.multipleHint'}
                    count={multiple.count}
                    values={{ total: node.userCount, primary: multiple.primary_here_count }}
                  />
                )}
              </span>
            </div>
          )}
        </div>

        {!summary ? (
          <p className={'workplace-membership__hint'}>
            {t(isFailed ? 'workplaces.membership.loadError' : 'workplaces.membership.loading')}
          </p>
        ) : (
          <table className={'workplace-membership__table'}>
            <thead>
              <tr>
                <th scope={'col'}>{t('workplaces.membership.jobTitle')}</th>
                {isTreeEnabled ? (
                  <>
                    <th scope={'col'}>{t('workplaces.membership.directColumn')}</th>
                    <th scope={'col'}>{t('workplaces.membership.subtreeColumn')}</th>
                  </>
                ) : (
                  <th scope={'col'}>{t('workplaces.membership.countColumn')}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {jobTitles.map((row) => (
                <tr key={row.id}>
                  <th scope={'row'}>{row.name}</th>
                  <td>{row.direct_count}</td>
                  {isTreeEnabled && <td>{row.subtree_count}</td>}
                </tr>
              ))}
              {showWithoutJobTitle && withoutJobTitle && (
                <tr className={'workplace-membership__row--none'}>
                  <th scope={'row'}>{t('workplaces.membership.withoutJobTitle')}</th>
                  <td>{withoutJobTitle.direct_count}</td>
                  {isTreeEnabled && <td>{withoutJobTitle.subtree_count}</td>}
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <th scope={'row'}>{t('workplaces.membership.total')}</th>
                <td>{summary.total.direct_count}</td>
                {isTreeEnabled && <td>{summary.total.subtree_count}</td>}
              </tr>
            </tfoot>
          </table>
        )}

        <p className={'workplace-membership__hint'}>{t('workplaces.detail.membershipHint')}</p>
      </div>
    </section>
  );
};
